import { sysColors } from "@/assets/theme/colors";
import { formatPhoneNumber } from "@/lib/phoneHelpers";
import { colorForTopic, formatCount, formatDayLabel } from "../_utils";
import type { CallTopicDay, CallTopicRecentCall, CallTopicStat, CallTopicWorkerStat } from "./queries";

export { colorForTopic };
export { formatCount, formatDayLabel, formatDuration, formatPercent, formatTotalDuration, percentOf } from "../_utils";

export const UNCLASSIFIED_TOPIC = "Unclassified";
export const UNKNOWN_WORKER_LABEL = "Unknown";

export type FilteredCallTopics = {
  topics: CallTopicStat[];
  total: number;
  classified: number;
  avgDurationSeconds: number | null;
  topTopic: CallTopicStat | null;
};

/** Slice topic buckets to the selected topics and recompute headline numbers. */
export function filterTopicsBySelection(topics: CallTopicStat[], selected: string[]): FilteredCallTopics {
  const selectedSet = new Set(selected);
  const filtered = topics.filter((bucket) => selectedSet.has(bucket.topic));
  const total = filtered.reduce((sum, bucket) => sum + bucket.count, 0);
  const classified = filtered
    .filter((bucket) => bucket.topic !== UNCLASSIFIED_TOPIC)
    .reduce((sum, bucket) => sum + bucket.count, 0);
  const durationWeighted = filtered.reduce((sum, bucket) => sum + (bucket.avgDurationSeconds ?? 0) * bucket.count, 0);
  const durationCalls = filtered.reduce(
    (sum, bucket) => sum + (bucket.avgDurationSeconds == null ? 0 : bucket.count),
    0
  );
  const topTopic = filtered.reduce<CallTopicStat | null>(
    (top, bucket) => (!top || bucket.count > top.count ? bucket : top),
    null
  );

  return {
    topics: filtered,
    total,
    classified,
    avgDurationSeconds: durationCalls === 0 ? null : durationWeighted / durationCalls,
    topTopic: topTopic && topTopic.count > 0 ? topTopic : null
  };
}

export type TopicBarRow = {
  name: string;
  Calls: number;
};

export function buildTopicBarData(topics: CallTopicStat[]): TopicBarRow[] {
  return [...topics]
    .filter((bucket) => bucket.count > 0)
    .sort((a, b) => b.count - a.count)
    .map((bucket) => ({ name: bucket.topic, Calls: bucket.count }));
}

export type TrendRow = {
  name: string;
} & Record<string, string | number>;

export type TrendSeries = {
  dataKey: string;
  name: string;
  color: string;
};

const TREND_TOPIC_LINES = 3;

/**
 * Daily rows for the volume trend: total plus the top selected topics.
 * Capped at a few topic lines so the chart stays readable with 13 topics selected.
 */
export function buildTrendData(
  days: CallTopicDay[],
  selected: string[],
  topTopics: string[]
): { rows: TrendRow[]; series: TrendSeries[] } {
  const selectedSet = new Set(selected);
  const lineTopics = topTopics.filter((topic) => selectedSet.has(topic)).slice(0, TREND_TOPIC_LINES);
  const rows = days.map((day) => {
    const counts = new Map(day.topics.map((entry) => [entry.topic, entry.count]));
    const row: TrendRow = { name: formatDayLabel(day.date), Total: 0 };
    for (const [topic, count] of counts) {
      if (selectedSet.has(topic)) row.Total = Number(row.Total) + count;
    }
    for (const topic of lineTopics) {
      row[topic] = counts.get(topic) ?? 0;
    }
    return row;
  });
  const series: TrendSeries[] = [
    { dataKey: "Total", name: "Total", color: sysColors["sys-brand-outline"] },
    ...lineTopics.map((topic) => ({ dataKey: topic, name: topic, color: colorForTopic(topic) }))
  ];
  return { rows, series };
}

export type SubtopicMetric = "calls" | "avgDuration" | "totalTime";

export type SubtopicMetricRow = {
  name: string;
  value: number;
};

const SUBTOPIC_METRIC_LABEL: Record<SubtopicMetric, string> = {
  calls: "Calls",
  avgDuration: "Avg minutes",
  totalTime: "Total minutes"
};

export function subtopicMetricLabel(metric: SubtopicMetric): string {
  return SUBTOPIC_METRIC_LABEL[metric];
}

const toMinutes = (seconds?: number | null): number => (seconds == null ? 0 : Math.round((seconds / 60) * 10) / 10);

/**
 * Subtopic rows for the drilldown chart under the selected metric lens.
 * Durations render as decimal minutes (the shared BarChart has no value
 * formatter); exact values live in the drilldown header stats line.
 */
export function buildSubtopicMetricData(bucket: CallTopicStat | null, metric: SubtopicMetric): SubtopicMetricRow[] {
  if (!bucket) return [];
  return [...bucket.subtopics]
    .filter((entry) => entry.count > 0)
    .map((entry) => {
      if (metric === "avgDuration") return { name: entry.subtopic, value: toMinutes(entry.avgDurationSeconds) };
      if (metric === "totalTime") return { name: entry.subtopic, value: toMinutes(entry.totalDurationSeconds) };
      return { name: entry.subtopic, value: entry.count };
    })
    .sort((a, b) => b.value - a.value);
}

export function interactionPathForCall(call: CallTopicRecentCall): string | null {
  if (!call.interactionId || !call.personPgaId) return null;
  return `/people/${encodeURIComponent(call.personPgaId)}/interactions/interactions/${encodeURIComponent(call.interactionId)}`;
}

export const UNKNOWN_PERSON_LABEL = "Unknown person";

export type NormalizedCallerIdentity = {
  name: string | null;
  phone: string | null;
};

/**
 * Shared caller normalization for collapsed rows and expanded details:
 * trimmed person name plus formatted call phone, with blank strings
 * counting as missing so surfaces never render empty identity.
 */
export function normalizeCallerIdentity(input: {
  personName?: string | null;
  callerPhone?: string | null;
}): NormalizedCallerIdentity {
  const name = input.personName?.trim() || null;
  const rawPhone = input.callerPhone?.trim() || null;
  return { name, phone: rawPhone ? formatPhoneNumber(rawPhone) : null };
}

/**
 * Collapsed-row caller identity, shared by desktop and compact layouts:
 * person name, else the formatted call phone, else Unknown person.
 */
export function formatCallerIdentity(call: Pick<CallTopicRecentCall, "personName" | "callerPhone">): string {
  const { name, phone } = normalizeCallerIdentity(call);
  return name ?? phone ?? UNKNOWN_PERSON_LABEL;
}

export function directionLabel(direction: string | null): string {
  if (direction === "inbound") return "Inbound";
  if (direction == null) return "—";
  return "Outbound";
}

export function workerDisplayName(workerName: string | null): string {
  return workerName ?? UNKNOWN_WORKER_LABEL;
}

export type WorkerOption = {
  value: string;
  label: string;
};

/**
 * Named workers only — the Unknown bucket has no worker name to filter by,
 * so it appears on the leaderboard but never as a filter choice.
 */
export function buildWorkerOptions(workers: CallTopicWorkerStat[]): WorkerOption[] {
  return workers
    .filter((entry) => entry.workerName != null)
    .map((entry) => ({
      value: entry.workerName as string,
      label: `${entry.workerName} (${formatCount(entry.calls)})`
    }));
}

/** Team-wide mean handle time, weighted by each member's ended calls. */
export function teamAverageDuration(workers: CallTopicWorkerStat[]): number | null {
  const weighted = workers.reduce((sum, entry) => sum + (entry.avgDurationSeconds ?? 0) * entry.calls, 0);
  const calls = workers.reduce((sum, entry) => sum + (entry.avgDurationSeconds == null ? 0 : entry.calls), 0);
  return calls === 0 ? null : weighted / calls;
}

export function teamTotalCalls(workers: CallTopicWorkerStat[]): number {
  return workers.reduce((sum, entry) => sum + entry.calls, 0);
}

export function formatSignedDuration(totalSeconds: number | null): string {
  if (totalSeconds == null) return "—";
  const rounded = Math.round(totalSeconds);
  if (rounded === 0) return "±0s";
  const sign = rounded < 0 ? "−" : "+";
  const abs = Math.abs(rounded);
  const minutes = Math.floor(abs / 60);
  const seconds = abs % 60;
  if (minutes === 0) return `${sign}${seconds}s`;
  return `${sign}${minutes}m ${seconds.toString().padStart(2, "0")}s`;
}

/** Member handle time against the team average, e.g. "−0:20 vs avg". */
export function durationDeltaText(memberAvg: number | null, teamAvg: number | null): string {
  if (memberAvg == null || teamAvg == null) return "—";
  return `${formatSignedDuration(memberAvg - teamAvg)} vs avg`;
}

/** Bar fill for the leaderboard share column, relative to the busiest member. */
export function workerShareOfMax(calls: number, maxCalls: number): number {
  if (maxCalls <= 0) return 0;
  return Math.min(1, Math.max(0, calls / maxCalls));
}

export type SentimentSlice = {
  key: "positive" | "neutral" | "negative" | "mixed";
  label: string;
  count: number;
  share: number;
};

/** Non-empty sentiment slices for the leaderboard stacked bar. */
export function buildSentimentSplit(worker: CallTopicWorkerStat): SentimentSlice[] {
  const total = worker.positiveCalls + worker.neutralCalls + worker.negativeCalls + worker.mixedCalls;
  if (total <= 0) return [];
  return (
    [
      { key: "positive", label: "Positive", count: worker.positiveCalls },
      { key: "neutral", label: "Neutral", count: worker.neutralCalls },
      { key: "negative", label: "Negative", count: worker.negativeCalls },
      { key: "mixed", label: "Mixed", count: worker.mixedCalls }
    ] as const
  )
    .filter((slice) => slice.count > 0)
    .map((slice) => ({ ...slice, share: slice.count / total }));
}
